import { useCallback, useMemo } from 'react';
import type { OUItem, OuScope } from '@/api/types';
import { collectDescendantItems } from '@/components/common/OuTree/ouTree';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useOUItems } from '@/hooks/useOUs';

/**
 * Sin perfil resuelto (modo mock / simulador de demo) no hay alcance que aplicar: se asume
 * acceso a todas las OUs para que la vista siga siendo navegable. En producción siempre hay
 * perfil, así que este valor no se usa.
 */
const DEMO_OU_SCOPE: OuScope = { rootOuIds: [], allOusAccess: true };

export interface ResolvedOuScope {
  /** true = sin restricción organizacional (admin central o alcance "Todas las OUs"). */
  isUnrestricted: boolean;
  /**
   * OUs que el usuario puede consultar: sus OUs raíz más todos sus descendientes. `null` cuando
   * no hay restricción. Un conjunto vacío significa "alcance configurado pero sin OUs válidas":
   * no debe verse ningún dato.
   */
  allowedOuIds: Set<string> | null;
  /** Catálogo de OUs recortado al alcance — alimenta filtros y árboles de los reportes. */
  scopedOuItems: OUItem[];
  /** El catálogo de OUs todavía no llegó y el alcance no se puede aplicar aún. */
  isResolving: boolean;
  /**
   * Alcance restringido que no resuelve a ninguna OU (administrador sin OU asignada, o con una
   * OU que ya no existe en el catálogo). No debe consultarse la API: sin ids, el backend
   * devolvería el tenant completo.
   */
  hasEmptyScope: boolean;
  /**
   * Alcance a enviar a la API de analítica combinado con la OU elegida en el filtro:
   * la OU seleccionada se expande a su subárbol y se intersecta con el alcance del usuario.
   * `undefined` = sin selección — no se manda ningún filtro, el backend resuelve el alcance completo
   * por su cuenta (más barato y más seguro que enumerarlo). Con una selección cuyo subárbol expandido
   * supera `MAX_EXPANDED_SCOPE_IDS`, se manda SIN expandir (ver comentario de esa constante).
   */
  resolveQueryOuIds: (selectedOuId: string) => string[] | undefined;
  /**
   * Igual que `resolveQueryOuIds`, pero para VARIAS OUs seleccionadas a la vez (filtro de OU con
   * selección múltiple, Fase 4 del plan de Reportería): cada una se expande a su propio subárbol, se
   * unen todos los ids, y el resultado se intersecta con el alcance del usuario. Lista vacía = sin
   * selección, mismo comportamiento que `resolveQueryOuIds('')`.
   */
  resolveQueryOuIdsMulti: (selectedOuIds: readonly string[]) => string[] | undefined;
}

/** Ids de `rootId` y de todos sus descendientes dentro del catálogo plano. */
function collectSubtreeIdsFromCatalog(items: OUItem[], rootId: string): string[] {
  return [rootId, ...collectDescendantItems(items, rootId).map(descendant => descendant.id)];
}

/**
 * 🔴 Incidente en producción (2026-09-0X): con el árbol completo de OUs (ver `rootsOnly: false` más
 * abajo), una OU raíz real (México, LATAM…) puede expandirse a CIENTOS de descendientes. Unirlos
 * todos en `scopeIds` (query string) puede superar el límite de longitud de la infraestructura, y el
 * servidor responde 404 en vez de procesar la petición — pasaba en TODOS los reportes al aplicar
 * cualquier filtro de OU amplio.
 *
 * Por encima de este tope, `resolveQueryOuIds`/`resolveQueryOuIdsMulti` mandan la selección SIN
 * expandir: el reporte queda acotado a las OUs elegidas literalmente, no a sus descendientes — el
 * mismo comportamiento (ya documentado, no nuevo) que existía antes de que este hook expandiera
 * subárboles. Evita el 404 a costa de exactitud solo en selecciones muy amplias; una BU o site
 * concreto sigue expandiéndose completo.
 */
const MAX_EXPANDED_SCOPE_IDS = 60;

/**
 * Alcance organizacional del usuario actual aplicado al catálogo de OUs.
 *
 * Regla de negocio: el admin central ve los reportes de todo el tenant; el AdminOU solo los de
 * las personas de las OUs que tenga configuradas en la pantalla de roles y de todos los niveles
 * inferiores de esas ramas del árbol. Si su alcance es "Todas", ve lo mismo que el admin central.
 */
export function useOuScope(): ResolvedOuScope {
  const currentUser = useCurrentUser();
  const ouScope = currentUser?.ouScope ?? DEMO_OU_SCOPE;
  // rootsOnly: false — `useOUItems` por omisión trae SOLO las 6 raíces (`esRaiz`, useOUs.ts): sin
  // esto, `scopedOuItems` nunca tenía hijos que anidar y el árbol de OU (Analytics genérico y
  // Reportería, los dos consumen este hook) se veía como una lista plana de 6 opciones sin flecha de
  // expandir. Mismo patrón ya usado por `AdminCampaigns.tsx`/`AdminRoles.tsx` para tener el árbol
  // completo de verdad.
  const { data: ouItems = [], isLoading: isLoadingOus } = useOUItems({ activeOnly: false, rootsOnly: false });

  const isUnrestricted = ouScope.allOusAccess;

  const allowedOuIds = useMemo<Set<string> | null>(() => {
    if (isUnrestricted) return null;
    const allowed = new Set<string>();
    ouScope.rootOuIds.forEach(rootOuId => {
      collectSubtreeIdsFromCatalog(ouItems, rootOuId).forEach(ouId => allowed.add(ouId));
    });
    return allowed;
  }, [isUnrestricted, ouScope.rootOuIds, ouItems]);

  const scopedOuItems = useMemo(
    () => (allowedOuIds === null ? ouItems : ouItems.filter(ouItem => allowedOuIds.has(ouItem.id))),
    [allowedOuIds, ouItems],
  );

  const resolveQueryOuIds = useCallback(
    (selectedOuId: string): string[] | undefined => {
      // Sin selección no se manda ningún filtro: el backend ya resuelve el alcance completo del
      // caller por su cuenta (claim `scopeOu`, ya expandido con descendientes por UserAdminService),
      // así que enumerarlo aquí y mandarlo por la URL solo duplica ese trabajo — y es exactamente lo
      // que producía el 404 para un admin con un alcance amplio y ningún filtro puesto.
      if (!selectedOuId) return undefined;

      const selectedSubtree = collectSubtreeIdsFromCatalog(ouItems, selectedOuId);
      const effective = allowedOuIds === null
        ? selectedSubtree
        : selectedSubtree.filter(ouId => allowedOuIds.has(ouId));

      if (effective.length > MAX_EXPANDED_SCOPE_IDS) {
        return allowedOuIds === null || allowedOuIds.has(selectedOuId) ? [selectedOuId] : [];
      }

      return effective;
    },
    [allowedOuIds, ouItems],
  );

  const resolveQueryOuIdsMulti = useCallback(
    (selectedOuIds: readonly string[]): string[] | undefined => {
      // Mismo razonamiento que `resolveQueryOuIds`: sin selección, nada que mandar.
      if (selectedOuIds.length === 0) return undefined;

      const expanded = new Set<string>();
      selectedOuIds.forEach(ouId => {
        collectSubtreeIdsFromCatalog(ouItems, ouId).forEach(id => expanded.add(id));
      });

      const effective = allowedOuIds === null
        ? [...expanded]
        : [...expanded].filter(ouId => allowedOuIds.has(ouId));

      if (effective.length > MAX_EXPANDED_SCOPE_IDS) {
        return allowedOuIds === null
          ? [...selectedOuIds]
          : selectedOuIds.filter(ouId => allowedOuIds.has(ouId));
      }

      return effective;
    },
    [allowedOuIds, ouItems],
  );

  const isResolving = !isUnrestricted && isLoadingOus;

  return {
    isUnrestricted,
    allowedOuIds,
    scopedOuItems,
    isResolving,
    hasEmptyScope: !isResolving && allowedOuIds !== null && allowedOuIds.size === 0,
    resolveQueryOuIds,
    resolveQueryOuIdsMulti,
  };
}
